import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminVenues from "./pages/admin/AdminVenues";
import AdminVenueCreate from "./pages/admin/AdminVenueCreate";
import AdminVenueDetail from "./pages/admin/AdminVenueDetail";
import AdminClients from "./pages/admin/AdminClients";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminIntegrationTests from "./pages/admin/AdminIntegrationTests";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import ClientLogin from "./pages/client/ClientLogin";
import ClientDashboard from "./pages/client/Dashboard";
import MembersList from "./pages/client/MembersList";
import MemberDetail from "./pages/client/MemberDetail";
import PassesList from "./pages/client/PassesList";
import ActivityLogs from "./pages/client/ActivityLogs";
import ProductsPage from "./pages/client/ProductsPage";
import ExpiringReport from "./pages/client/ExpiringReport";
import Profile from "./pages/client/Profile";
import StaffManagement from "./pages/client/StaffManagement";
import PinLookup from "./pages/client/PinLookup";
import ResetPin from "./pages/client/ResetPin";
import AnalysisCoverageReport from "./pages/client/AnalysisCoverageReport";
import CardsExpiringReport from "./pages/client/CardsExpiringReport";
import DataQualityReport from "./pages/client/DataQualityReport";
import LiveFloor from "./pages/client/LiveFloor";
import SyncAwarenessReport from "./pages/client/SyncAwarenessReport";
import DemographicsReport from "./pages/client/DemographicsReport";
import AcquisitionReport from "./pages/client/AcquisitionReport";
import DuplicatesReport from "./pages/client/DuplicatesReport";
import BulkPointsAward from "./pages/client/BulkPointsAward";
import SettingsBranding from "./pages/client/settings/Branding";
import SettingsPortal from "./pages/client/settings/PortalFeatures";
import SettingsEmails from "./pages/client/settings/Emails";
import SettingsAnalysisCodes from "./pages/client/settings/AnalysisCodes";
import SettingsDigitalCard from "./pages/client/settings/DigitalCard";
import SettingsTermsAndConditions from "./pages/client/settings/TermsAndConditions";
import SettingsCommunications from "./pages/client/settings/Communications";
import VenueSignup from "./pages/public/VenueSignup";
import IgtChangePin from "./pages/client/igt/ChangePin";
import IgtFindMember from "./pages/client/igt/FindMember";
import IgtVerifyPin from "./pages/client/igt/VerifyPin";
import IgtUpdateProfile from "./pages/client/igt/UpdateProfile";
import "./App.css";

function ProtectedRoute({ children, role }) {
  const { user } = useAuth();
  const loginPath = role === "admin" ? "/admin/login" : "/client/login";
  if (!user) return <Navigate to={loginPath} />;
  // Sessions are namespaced per portal, so a mismatch shouldn't happen — but
  // if it ever does, send them to THIS portal's login, never the other one.
  if (user.role !== role) return <Navigate to={loginPath} />;
  return children;
}

function ClientRoute({ children }) {
  return <ProtectedRoute role="client">{children}</ProtectedRoute>;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Admin portal */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/venues" element={<ProtectedRoute role="admin"><AdminVenues /></ProtectedRoute>} />
            <Route path="/admin/venues/new" element={<ProtectedRoute role="admin"><AdminVenueCreate /></ProtectedRoute>} />
            <Route path="/admin/venues/:id" element={<ProtectedRoute role="admin"><AdminVenueDetail /></ProtectedRoute>} />
            <Route path="/admin/clients" element={<ProtectedRoute role="admin"><AdminClients /></ProtectedRoute>} />
            <Route path="/admin/integration-tests" element={<ProtectedRoute role="admin"><AdminIntegrationTests /></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute role="admin"><AdminAnalytics /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute role="admin"><AdminSettings /></ProtectedRoute>} />

            {/* Client portal — auth */}
            <Route path="/client/login" element={<ClientLogin />} />
            <Route path="/portal/:slug" element={<ClientLogin />} />

            {/* Client portal — pages */}
            <Route path="/client" element={<ClientRoute><ClientDashboard /></ClientRoute>} />
            <Route path="/client/members" element={<ClientRoute><MembersList /></ClientRoute>} />
            <Route path="/client/members/:membershipId" element={<ClientRoute><MemberDetail /></ClientRoute>} />
            <Route path="/client/s7000/pin-lookup" element={<ClientRoute><PinLookup /></ClientRoute>} />
            <Route path="/client/s7000/reset-pin" element={<ClientRoute><ResetPin /></ClientRoute>} />
            <Route path="/client/igt/find-member"    element={<ClientRoute><IgtFindMember    /></ClientRoute>} />
            <Route path="/client/igt/verify-pin"     element={<ClientRoute><IgtVerifyPin     /></ClientRoute>} />
            <Route path="/client/igt/change-pin"     element={<ClientRoute><IgtChangePin     /></ClientRoute>} />
            <Route path="/client/igt/update-profile" element={<ClientRoute><IgtUpdateProfile /></ClientRoute>} />
            <Route path="/client/passes" element={<ClientRoute><PassesList /></ClientRoute>} />
            <Route path="/client/logs" element={<ClientRoute><ActivityLogs /></ClientRoute>} />
            <Route path="/client/products" element={<ClientRoute><ProductsPage /></ClientRoute>} />
            <Route path="/client/reports/expiring" element={<ClientRoute><ExpiringReport /></ClientRoute>} />
            <Route path="/client/reports/cards-expiring" element={<ClientRoute><CardsExpiringReport /></ClientRoute>} />
            <Route path="/client/reports/acquisition" element={<ClientRoute><AcquisitionReport /></ClientRoute>} />
            <Route path="/client/s7000/reports/analysis-coverage" element={<ClientRoute><AnalysisCoverageReport /></ClientRoute>} />
            <Route path="/client/s7000/reports/data-quality" element={<ClientRoute><DataQualityReport /></ClientRoute>} />
            <Route path="/client/s7000/reports/sync-awareness" element={<ClientRoute><SyncAwarenessReport /></ClientRoute>} />
            <Route path="/client/s7000/reports/demographics" element={<ClientRoute><DemographicsReport /></ClientRoute>} />
            <Route path="/client/s7000/duplicates" element={<ClientRoute><DuplicatesReport /></ClientRoute>} />
            <Route path="/client/s7000/live" element={<ClientRoute><LiveFloor /></ClientRoute>} />
            <Route path="/client/s7000/points/bulk" element={<ClientRoute><BulkPointsAward /></ClientRoute>} />
            <Route path="/client/settings/profile" element={<ClientRoute><Profile /></ClientRoute>} />
            <Route path="/client/settings/staff" element={<ClientRoute><StaffManagement /></ClientRoute>} />
            <Route path="/client/settings/portal" element={<ClientRoute><SettingsPortal /></ClientRoute>} />
            <Route path="/client/settings/branding" element={<ClientRoute><SettingsBranding /></ClientRoute>} />
            <Route path="/client/settings/emails" element={<ClientRoute><SettingsEmails /></ClientRoute>} />
            <Route path="/client/settings/analysis-codes" element={<ClientRoute><SettingsAnalysisCodes /></ClientRoute>} />
            <Route path="/client/settings/digital-card" element={<ClientRoute><SettingsDigitalCard /></ClientRoute>} />
            <Route path="/client/settings/terms" element={<ClientRoute><SettingsTermsAndConditions /></ClientRoute>} />
            <Route path="/client/settings/communications" element={<ClientRoute><SettingsCommunications /></ClientRoute>} />

            {/* Public signup — per venue */}
            <Route path="/:slug/*" element={<VenueSignup />} />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/admin/login" />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
